import { useState, useRef, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { Users, X, Copy, Check, CheckCircle2, XCircle } from "lucide-react";
import { useTran } from "@/hooks";
import { Button } from "@/components/ui/button";
import { useShareSurveyStore } from "../hooks/useShareSurveyStore";
const useCopy = () => {
  const [copiedEmail, setCopiedEmail] = useState(null);
  const copy = (text, email) => {
    navigator.clipboard.writeText(text);
    setCopiedEmail(email);
    setTimeout(() => setCopiedEmail(null), 2000);
  };
  return { copiedEmail, copy };
};
const ShareSurveyInternalTab = observer(({ onSendInvites }) => {
  const { t } = useTran();
  const { copiedEmail, copy } = useCopy();
  const shareStore = useShareSurveyStore();
  const [search, setSearch] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // ✅ Fetch all suggestions on mount
  useEffect(() => {
    shareStore.fetchSuggestions();
  }, []);

  // ✅ Safe array access with fallback
  const inviteSuggestions = Array.isArray(shareStore.inviteSuggestions)
    ? shareStore.inviteSuggestions
    : [];

  // ✅ Filter from inviteSuggestions by search + exclude selected
  const suggestions = inviteSuggestions.filter(
    (u) =>
      !shareStore.selectedInvites.has(u.email) &&
      (search.trim() === "" ||
        u.name?.toLowerCase().includes(search.toLowerCase()) ||
        u.email?.toLowerCase().includes(search.toLowerCase())),
  );

  // ✅ Get selected user objects from inviteSuggestions
  const selectedUsers = inviteSuggestions.filter((u) =>
    shareStore.selectedInvites.has(u.email),
  );

  // ── Close dropdown on outside click ──
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (user) => {
    shareStore.toggleInternal(user.email);
    setSearch("");
    inputRef.current?.focus();
  };

  const handleClearAll = () => {
    shareStore.clearInvites();
    setSearch("");
  };

  // ✅ Only filter locally, no API call on each keystroke
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value); // ✅ local filter only
    setShowDropdown(true);
  };
  const results = Array.isArray(shareStore.inviteResults)
    ? shareStore.inviteResults
    : [];

  return (
    <>
      {/* ── Content Box ── */}
      <div
        className="flex-1 rounded-lg border border-slate-200 bg-slate-50/70 p-4
                            dark:border-slate-700 dark:bg-slate-900/40 flex flex-col min-h-0"
      >
        <p className="text-sm font-semibold text-foreground">
          {t("Search internal users")}
        </p>
        <p className="text-xs text-muted-foreground mt-0.5">
          {t(
            "Select one or more existing internal users. Only selected users will be able to take this survey.",
          )}
        </p>

        {/* ── Multi Select Box ── */}
        <div ref={containerRef} className="relative mt-3">
          <div
            onClick={() => {
              inputRef.current?.focus();
              setShowDropdown(true);
            }}
            className={`min-h-[42px] w-full flex flex-wrap items-center gap-1.5 px-3 py-2
                                    rounded-lg border bg-background cursor-text transition-all
                                    ${
                                      showDropdown
                                        ? "border-indigo-400 ring-2 ring-indigo-500/20 dark:border-indigo-500"
                                        : "border-border dark:border-slate-700"
                                    }`}
          >
            {/* ── Selected Tags ── */}
            {selectedUsers.map((user) => (
              <span
                key={user._id ?? user.email}
                className="inline-flex items-center gap-1.5 pl-1.5 pr-1 py-0.5
                                           rounded-full text-xs font-medium
                                           bg-indigo-100 text-indigo-700 border border-indigo-200
                                           dark:bg-indigo-500/20 dark:text-indigo-300 dark:border-indigo-500/30"
              >
                <span
                  className="w-4 h-4 rounded-full bg-indigo-500 text-white
                                                 flex items-center justify-center text-[10px] font-bold flex-shrink-0"
                >
                  {user.name?.charAt(0).toUpperCase() || "?"}
                </span>
                <span className="max-w-[100px] truncate">
                  {user.name || user.email}
                </span>
                <button
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    shareStore.toggleInternal(user.email);
                  }}
                  className="rounded-full p-0.5 hover:bg-indigo-200
                                               dark:hover:bg-indigo-500/30 transition-colors"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}

            {/* ── Search Input ── */}
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={handleSearchChange} // ✅ local filter only
              onFocus={() => setShowDropdown(true)}
              placeholder={
                selectedUsers.length === 0
                  ? t("Search by name or email...")
                  : ""
              }
              className="flex-1 min-w-[120px] text-sm bg-transparent outline-none
                                       placeholder:text-muted-foreground"
            />

            {/* ── Clear All ── */}
            {selectedUsers.length > 0 && (
              <button
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleClearAll();
                }}
                className="ml-auto flex items-center gap-1 text-xs text-muted-foreground
                                           hover:text-foreground transition-colors flex-shrink-0"
              >
                <X className="w-3 h-3" />
                {t("Clear all")}
              </button>
            )}
          </div>

          {/* ── Dropdown ── */}
          {showDropdown && (
            <div
              className="absolute z-50 top-full left-0 right-0 mt-1
                                        rounded-lg border border-border bg-white dark:bg-gray-900
                                        shadow-lg overflow-hidden max-h-52 overflow-y-auto"
            >
              {/* Loading skeleton */}
              {shareStore.suggestionsLoading && (
                <div className="space-y-1 p-2">
                  {[1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className="h-10 rounded-md bg-muted/40 animate-pulse"
                    />
                  ))}
                </div>
              )}

              {/* Empty state */}
              {!shareStore.suggestionsLoading && suggestions.length === 0 && (
                <div
                  className="flex items-center gap-2 px-4 py-3
                                                text-xs text-muted-foreground"
                >
                  <Users className="w-4 h-4" />
                  {search
                    ? t("No users match your search.")
                    : t("All users have been selected.")}
                </div>
              )}

              {/* Suggestion rows */}
              {!shareStore.suggestionsLoading &&
                suggestions.map((user) => (
                  <div
                    key={user.email}
                    onMouseDown={() => handleSelect(user)}
                    className="flex items-center gap-3 px-3 py-2.5 cursor-pointer
                                               hover:bg-indigo-50 dark:hover:bg-indigo-500/10
                                               transition-colors border-b border-border last:border-0"
                  >
                    <div
                      className="w-7 h-7 rounded-full flex-shrink-0
                                                    bg-indigo-100 text-indigo-600
                                                    dark:bg-indigo-500/20 dark:text-indigo-400
                                                    flex items-center justify-center text-xs font-bold"
                    >
                      {user.name?.charAt(0).toUpperCase() || "?"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-foreground truncate">
                        {user.name}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {user.email}
                      </p>
                    </div>
                    <span className="text-xs text-indigo-400 font-medium flex-shrink-0">
                      + {t("Add")}
                    </span>
                  </div>
                ))}
            </div>
          )}
        </div>

        {/* ── Error ── */}
        {shareStore.internalError && (
          <p className="mt-3 text-xs text-destructive">
            {shareStore.internalError}
          </p>
        )}
        {/* ── ✅ Delivery Results ── */}
        {results.length > 0 && (
          <div className="mt-4 flex flex-col gap-2 min-h-0">
            {/* Header row */}
            <div className="flex items-center justify-between flex-shrink-0">
              <p className="text-xs font-semibold text-foreground">
                {t("Invite links")}
                <span className="ml-1.5 text-muted-foreground font-normal">
                  ({results.length})
                </span>
              </p>
              <button
                onClick={() => shareStore.clearInviteResults()}
                className="text-xs text-muted-foreground hover:text-foreground
                           flex items-center gap-1 transition-colors"
              >
                <X className="w-3 h-3" /> {t("Dismiss")}
              </button>
            </div>

            {/* ✅ Fixed: max-height + scroll so footer stays visible */}
            <ul
              className="max-h-[180px] overflow-y-auto divide-y divide-border
                       rounded-lg border border-border
                       bg-white dark:bg-gray-900 text-xs"
            >
              {results.map((item) => (
                <li
                  key={item.email}
                  className="flex items-center gap-3 px-3 py-2.5"
                >
                  {/* Status icon */}
                  {item.success ? (
                    <CheckCircle2 className="w-4 h-4 flex-shrink-0 text-emerald-500" />
                  ) : (
                    <XCircle className="w-4 h-4 flex-shrink-0 text-rose-400" />
                  )}

                  {/* Email — fixed width */}
                  <span className="w-40 flex-shrink-0 font-medium text-foreground truncate">
                    {item.email}
                  </span>

                  {/* Invite URL — fills remaining space, truncated */}
                  {item.inviteUrl ? (
                    <span
                      className="flex-1 truncate font-mono text-muted-foreground
                                         min-w-0"
                    >
                      {item.inviteUrl}
                    </span>
                  ) : (
                    <span className="flex-1 truncate text-rose-400 italic min-w-0">
                      {item.error || t("Failed")}
                    </span>
                  )}

                  {/* Copy button */}
                  {item.inviteUrl && (
                    <button
                      onClick={() => copy(item.inviteUrl, item.email)}
                      title={t("Copy link")}
                      className="flex-shrink-0 p-1 rounded
                                       hover:bg-slate-100 dark:hover:bg-slate-800
                                       transition-colors text-muted-foreground
                                       hover:text-foreground"
                    >
                      {copiedEmail === item.email ? (
                        <Check className="w-3.5 h-3.5 text-emerald-500" />
                      ) : (
                        <Copy className="w-3.5 h-3.5" />
                      )}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* ── Footer ── */}
      <div className="mt-4 flex items-center justify-between">
        <span
          className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors
                    ${
                      shareStore.selectedInvitesCount > 0
                        ? "bg-indigo-600 text-white"
                        : "bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500"
                    }`}
        >
          {shareStore.selectedInvitesCount} {t("selected")}
        </span>

        <Button
          onClick={onSendInvites}
          disabled={
            shareStore.inviteLoading || shareStore.selectedInvitesCount === 0
          }
          className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-40"
        >
          {shareStore.inviteLoading ? t("Sending...") : t("Send Invites")}
        </Button>
      </div>
    </>
  );
});

export default ShareSurveyInternalTab;
